import { BarChart3Icon, LockKeyholeIcon } from "lucide-react";
import { CartesianGrid, LabelList, Line, LineChart, XAxis } from "recharts";

import { EmptyState } from "@/components/EmptyState";
import { ChartContainer } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { useWeeklyScoresQuery } from "@/hooks/queries";

type WeeklyChartProps = {
  isLocked?: boolean;
  onLockedClick?: () => void;
};

export function WeeklyChart({ isLocked = false, onLockedClick }: WeeklyChartProps) {
  const { data = [], isPending } = useWeeklyScoresQuery();

  const chartContent = isPending ? (
    <Skeleton className="h-40 w-full rounded-lg" />
  ) : data.every(d => d.score === 0) ? (
    <EmptyState
      icon={BarChart3Icon}
      title="이번 주 테스트 기록이 없어요"
      description="테스트를 완료하면 점수가 표시돼요!"
    />
  ) : (
    <ChartContainer
      config={{
        score: {
          label: "점수",
          color: "var(--color-primary, #0065b3)",
        },
      }}
      className="h-40 w-full"
    >
      <LineChart data={data} margin={{ top: 20, left: 16, right: 16, bottom: 0 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis dataKey="day" tickLine={false} axisLine={false} tickMargin={8} tick={{ fontSize: 12 }} />
        <Line
          dataKey="score"
          type="monotone"
          stroke="var(--color-score)"
          strokeWidth={2}
          dot={{ fill: "var(--color-score)", r: 4 }}
          activeDot={{ r: 6 }}
          connectNulls={false}
        >
          <LabelList
            dataKey="score"
            position="top"
            offset={10}
            className="fill-foreground"
            fontSize={11}
            formatter={value => (value != null ? `${value}` : "")}
          />
        </Line>
      </LineChart>
    </ChartContainer>
  );

  return (
    <div className="rounded-xl bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <p className="text-base font-semibold text-foreground">이번 주 성적</p>
          <p className="text-xs text-muted-foreground">주간 테스트 점수 추이</p>
        </div>
      </div>

      {isLocked ? (
        <div className="relative cursor-pointer" onClick={onLockedClick}>
          <div className="pointer-events-none select-none blur-md">{chartContent}</div>

          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <LockKeyholeIcon className="size-5 text-muted-foreground" />
            <p className="text-sm font-semibold text-foreground">Plus로 주간 분석 보기</p>
          </div>
        </div>
      ) : (
        chartContent
      )}
    </div>
  );
}
